import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobBoardApi } from '../../services/api';
import {
  PageHeader,
  EmptyState,
  Spinner,
  Modal,
} from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  BriefcaseIcon,
  ArrowRightCircleIcon,
} from '@heroicons/react/24/outline';

const APP_STATUS = {
  submitted:    'bg-gray-100 text-gray-700 border-gray-200',
  under_review: 'bg-amber-100 text-amber-800 border-amber-200',
  interviewing: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  offered:      'bg-green-100 text-green-800 border-green-200',
  rejected:     'bg-red-100 text-red-800 border-red-200',
  withdrawn:    'bg-gray-100 text-gray-500 border-gray-200',
};

function StatusChip({ value }) {
  const c = APP_STATUS[value] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize', c)}>
      {value?.replace('_', ' ')}
    </span>
  );
}

export default function JobBoardHome() {
  const qc = useQueryClient();
  const [applyFor, setApplyFor] = useState(null);
  const [cover, setCover]       = useState('');

  const { data: postings = [], isLoading, error } = useQuery({
    queryKey: ['my-job-matches'],
    queryFn:  () => jobBoardApi.myMatches().then(r => r.data),
  });

  const applyMut = useMutation({
    mutationFn: ({ id, cover_letter }) => jobBoardApi.apply(id, { cover_letter }),
    onSuccess:  () => {
      toast.success('Application submitted');
      qc.invalidateQueries({ queryKey: ['my-job-matches'] });
      setApplyFor(null);
      setCover('');
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to apply'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Job Board"
        subtitle="Open internal postings, ranked by how well your skills match."
      />

      {error ? (
        <EmptyState title="Unable to load postings" description="Please try again later." />
      ) : !postings.length ? (
        <EmptyState
          title="No open postings"
          description="There are no open internal postings right now. Check back later."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {postings.map(p => (
            <div key={p.id} className="card p-5 flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <BriefcaseIcon className="w-4 h-4 text-blue-600 shrink-0" />
                    <p className="font-semibold text-gray-900 truncate">{p.title}</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    {p.department?.name || '—'} · {p.location || (p.is_remote ? 'Remote' : '—')}
                  </p>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <p className="text-2xl font-bold text-blue-600">{p.match_pct}%</p>
                  <p className="text-[10px] text-gray-400 uppercase">match</p>
                </div>
              </div>

              {p.matched_skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {p.matched_skills.slice(0, 6).map(s => (
                    <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-green-50 text-green-700 border border-green-100 capitalize">
                      {s}
                    </span>
                  ))}
                </div>
              )}

              {p.description && (
                <p className="text-sm text-gray-600 line-clamp-3 mb-3 flex-1">{p.description}</p>
              )}

              <div className="flex items-center justify-between mt-auto pt-2">
                <Link to={`/app/job-board/${p.id}`} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                  Details <ArrowRightCircleIcon className="w-3.5 h-3.5" />
                </Link>
                {p.application_status ? (
                  <StatusChip value={p.application_status} />
                ) : (
                  <button
                    className="btn-sm btn-primary"
                    onClick={() => { setApplyFor(p); setCover(''); }}
                  >
                    Apply
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!applyFor} onClose={() => setApplyFor(null)} title={applyFor ? `Apply · ${applyFor.title}` : 'Apply'} size="md">
        {applyFor && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">{applyFor.title}</p>
              <p className="text-xs text-gray-500">{applyFor.department?.name || '—'}</p>
            </div>
            <div>
              <label className="label">Cover letter (optional)</label>
              <textarea
                className="input h-32 resize-none"
                value={cover}
                onChange={e => setCover(e.target.value)}
                placeholder="Why are you a good fit?"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setApplyFor(null)} className="btn-secondary">Cancel</button>
              <button
                disabled={applyMut.isPending}
                onClick={() => applyMut.mutate({ id: applyFor.id, cover_letter: cover })}
                className="btn-primary disabled:opacity-40"
              >
                {applyMut.isPending ? 'Submitting…' : 'Submit application'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
